import {
  shouldAutoStartSso,
  ssoAutoStartUrl,
} from "@/wab/client/components/pages/sso-auto-start";
import * as React from "react";

export interface AuthEntryPolicy {
  mode?: string;
  tenantId?: string;
}

type EntryState = "loading" | "unavailable" | "private";

/**
 * Renders the Studio credential sign-in / sign-up content only once the server
 * (`getAuthEntry`, the single source of the deployment auth mode) has answered
 * with the explicit `private` mode. While loading, and for a public, missing,
 * unknown or failed policy, only a neutral no-form state is shown: there is no
 * fallback to the native form. A public deployment with one configured tenant
 * (and a private one without `?sso=off`) starts that tenant's SSO. Recovery,
 * verification and SSO pages do not use this gate.
 */
export function AuthEntryGate(props: {
  mode: "sign in" | "sign up";
  nextPath: string;
  search: string;
  getAuthEntry: () => Promise<AuthEntryPolicy>;
  navigate: (url: string) => void;
  children: React.ReactNode;
}) {
  const { mode, nextPath, search, getAuthEntry, navigate } = props;
  const [state, setState] = React.useState<EntryState>("loading");

  React.useEffect(() => {
    let cancelled = false;
    setState("loading");
    new Promise<AuthEntryPolicy>((resolve) => resolve(getAuthEntry())).then(
      ({ mode: entryMode, tenantId }) => {
        if (cancelled) {
          return;
        }
        // A public deployment always enters through the SSO (`?sso=off` is not
        // an escape hatch there); a private one honours `?sso=off`.
        const start =
          mode === "sign in" &&
          !!tenantId &&
          (entryMode === "public-sso" ||
            (entryMode === "private" && shouldAutoStartSso(mode, search)));
        if (start && tenantId) {
          navigate(ssoAutoStartUrl(tenantId, nextPath));
        }
        setState(entryMode === "private" ? "private" : "unavailable");
      },
      () => {
        if (!cancelled) {
          setState("unavailable");
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [mode]);

  if (state === "private") {
    return <>{props.children}</>;
  }
  return (
    <div className={"LoginForm__Controls"}>
      {state === "loading" ? (
        <p role={"status"}>Please wait…</p>
      ) : (
        <div role={"alert"}>
          <h1>Sign-in is unavailable</h1>
          <p>Please try again later.</p>
        </div>
      )}
    </div>
  );
}
